import React from 'react';
import { 
  Edit, 
  Trash2, 
  Eye, 
  GraduationCap, 
  Calendar, 
  MapPin, 
  Clock, 
  User,
  Users,
  BookOpen,
  Award,
  ChevronRight
} from 'lucide-react';

const SoutenanceCard = ({ soutenance, onEdit, onDelete, onViewDetails }) => {
  const getStatutColor = (statut) => {
    const colors = {
      'PLANIFIEE': 'bg-blue-100 text-blue-800 border-blue-200',
      'EN_COURS': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'TERMINEE': 'bg-green-100 text-green-800 border-green-200',
      'REPORTEE': 'bg-orange-100 text-orange-800 border-orange-200',
      'ANNULEE': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[statut] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatDuree = (minutes) => {
    if (!minutes && minutes !== 0) return 'Non définie';
    const heures = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return heures > 0 ? `${heures}h${mins > 0 ? mins.toString().padStart(2, '0') : ''}` : `${mins}min`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date non définie';
    
    try {
      // Correction pour plusieurs formats de date problématiques
      let correctedDateString = dateString;
      
      // Correction pour les années commençant par 0 (ex: 0205 -> 2025)
      if (dateString.match(/^0\d{3}-/)) {
        correctedDateString = `2${dateString.substring(1)}`;
      }
      
      // Correction pour les formats avec timezone bizarre (+00:13:35)
      correctedDateString = correctedDateString.replace(/\+00:\d{2}:\d{2}/, '+00:00');
      
      // Si la date est toujours invalide, essayons de l'extraire manuellement
      const date = new Date(correctedDateString);
      if (isNaN(date.getTime())) {
        // Extraction manuelle avec regex
        const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})T?/);
        if (match) {
          let [_, year, month, day] = match;
          // Si l'année commence par 0, corrigeons-la
          if (year.startsWith('0')) {
            year = `2${year.substring(1)}`;
          }
          return `${day}/${month}/${year}`;
        }
        return 'Date invalide';
      }
      
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      console.error("Erreur lors du formatage de la date:", e, dateString);
      return 'Date invalide';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'Heure non définie';
    
    try {
      // Correction pour plusieurs formats de date problématiques
      let correctedDateString = dateString;
      
      // Correction pour les années commençant par 0 (ex: 0205 -> 2025)
      if (dateString.match(/^0\d{3}-/)) {
        correctedDateString = `2${dateString.substring(1)}`;
      }
      
      // Correction pour les formats avec timezone bizarre (+00:13:35)
      correctedDateString = correctedDateString.replace(/\+00:\d{2}:\d{2}/, '+00:00');
      
      // Si la date est toujours invalide, essayons d'extraire l'heure manuellement
      const date = new Date(correctedDateString);
      if (isNaN(date.getTime())) {
        // Extraction manuelle avec regex
        const match = dateString.match(/T(\d{2}):(\d{2}):(\d{2})/);
        if (match) {
          const [_, hours, minutes] = match;
          return `${hours}:${minutes}`;
        }
        return 'Heure invalide';
      }
      
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error("Erreur lors du formatage de l'heure:", e, dateString);
      return 'Heure invalide';
    }
  };

  const getEtudiantsDisplay = () => {
    if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
      const etudiants = soutenance.etudiants_soutenance
        .filter(es => es && es.etudiant)
        .sort((a, b) => (a.ordre_presentation || 0) - (b.ordre_presentation || 0))
        .slice(0, 3);
      
      const noms = etudiants.map(es => {
        if (es && es.etudiant) {
          return `${es.etudiant.nom || ''} ${es.etudiant.prenom || ''}`.trim();
        }
        return '';
      }).filter(Boolean).join(', ');
      
      if ((soutenance.etudiants_soutenance || []).length > 3) {
        return `${noms} et ${soutenance.etudiants_soutenance.length - 3} autre(s)`;
      }
      return noms || 'Étudiants non définis';
    } else if (soutenance.etudiant) {
      return `${soutenance.etudiant.nom || ''} ${soutenance.etudiant.prenom || ''}`.trim() || 'Étudiant non défini';
    }
    return 'Étudiants non définis';
  };

  const getRolesPrincipaux = () => {
    if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
      const roles = soutenance.etudiants_soutenance
        .filter(es => es && es.role === 'PRINCIPAL' && es.etudiant)
        .map(es => `${es.etudiant.nom || ''} ${es.etudiant.prenom || ''}`.trim())
        .filter(Boolean);
      
      if (roles.length > 0) {
        return `Principal: ${roles.join(', ')}`;
      }
      return `${soutenance.etudiants_soutenance.length} participant(s)`;
    }
    return '';
  };

  const isCollective = soutenance.est_collective || 
                      (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 1) ||
                      (soutenance.nombre_etudiants && soutenance.nombre_etudiants > 1);

  const nombreEtudiants = soutenance.nombre_etudiants || 
                          (soutenance.etudiants_soutenance ? soutenance.etudiants_soutenance.length : 0);

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 overflow-hidden">
      {/* Bandeau supérieur avec statut */}
      <div className={`w-full h-1.5 ${getStatutColor(soutenance.statut).split(' ')[0]}`}></div>
      
      <div className="p-5">
        <div className="flex items-start">
          {/* Icône et titre */}
          <div className="flex-1">
            <div className="flex items-center mb-3">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${isCollective ? 'bg-purple-100' : 'bg-blue-100'} mr-3`}>
                {isCollective ? (
                  <Users className={`h-5 w-5 ${isCollective ? 'text-purple-600' : 'text-blue-600'}`} />
                ) : (
                  <GraduationCap className={`h-5 w-5 ${isCollective ? 'text-purple-600' : 'text-blue-600'}`} />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                  {soutenance.titre_travail || 'Titre non défini'}
                </h3>
                <p className="text-sm text-gray-600">
                  {getEtudiantsDisplay()}
                </p>
              </div>
            </div>

            {/* Badges et indicateurs */}
            <div className="flex flex-wrap gap-2 mb-4">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatutColor(soutenance.statut)}`}>
                {soutenance.statut_display || soutenance.statut || 'PLANIFIEE'}
              </span>
              
              {isCollective && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                  <Users className="h-3 w-3 mr-1" />
                  {nombreEtudiants} étudiant{nombreEtudiants > 1 ? 's' : ''}
                </span>
              )}
              
              {soutenance.type_soutenance && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                  <BookOpen className="h-3 w-3 mr-1" />
                  {soutenance.type_soutenance?.nom}
                </span>
              )}
              
              {soutenance.note_finale && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                  <Award className="h-3 w-3 mr-1" />
                  {soutenance.note_finale}/20
                </span>
              )}
            </div>

            {/* Informations principales */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 text-gray-500 mr-2" />
                <span className="text-gray-700">{formatDate(soutenance.date_soutenance)}</span>
              </div>
              
              <div className="flex items-center text-sm">
                <Clock className="h-4 w-4 text-gray-500 mr-2" />
                <span className="text-gray-700">{formatTime(soutenance.date_soutenance)}</span>
              </div>
              
              <div className="flex items-center text-sm">
                <MapPin className="h-4 w-4 text-gray-500 mr-2" />
                <span className="text-gray-700">{soutenance.salle?.libelle || soutenance.salle?.nom || 'Salle non définie'}</span>
              </div>
              
              <div className="flex items-center text-sm">
                <Clock className="h-4 w-4 text-gray-500 mr-2" />
                <span className="text-gray-700">Durée: {formatDuree(soutenance.duree_prevue)}</span>
              </div>
            </div>

            {/* Encadrement */}
            <div className="border-t border-gray-100 pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center text-sm">
                <User className="h-4 w-4 text-gray-500 mr-2" />
                <div>
                  <span className="text-gray-500">Directeur:</span>
                  <span className="text-gray-700 ml-1">
                    {soutenance.directeur_info?.nom || 
                    (soutenance.directeur_interne && `${soutenance.directeur_interne.prenom || ''} ${soutenance.directeur_interne.nom || ''}`.trim()) ||
                    (soutenance.directeur_externe && soutenance.directeur_externe.utilisateur && 
                      `${soutenance.directeur_externe.utilisateur.first_name || ''} ${soutenance.directeur_externe.utilisateur.last_name || ''}`.trim()) ||
                    'Non défini'}
                  </span>
                </div>
              </div>

              {(soutenance.co_directeur_interne || soutenance.co_directeur_externe || soutenance.co_directeur_info) && (
                <div className="flex items-center text-sm">
                  <User className="h-4 w-4 text-gray-500 mr-2" />
                  <div>
                    <span className="text-gray-500">Co-directeur:</span>
                    <span className="text-gray-700 ml-1">
                      {soutenance.co_directeur_info?.nom || 
                      (soutenance.co_directeur_interne && `${soutenance.co_directeur_interne.prenom || ''} ${soutenance.co_directeur_interne.nom || ''}`.trim()) ||
                      (soutenance.co_directeur_externe && soutenance.co_directeur_externe.utilisateur && 
                        `${soutenance.co_directeur_externe.utilisateur.first_name || ''} ${soutenance.co_directeur_externe.utilisateur.last_name || ''}`.trim()) ||
                      'Non défini'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Actions */}
          <div className="ml-4 flex flex-col space-y-2">
            <button
              onClick={() => onViewDetails(soutenance)}
              className="p-2 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
              title="Voir les détails"
            >
              <Eye className="h-5 w-5" />
            </button>
            <button
              onClick={() => onEdit(soutenance)}
              className="p-2 rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
              title="Modifier"
            >
              <Edit className="h-5 w-5" />
            </button>
            <button
              onClick={() => onDelete(soutenance)}
              className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
              title="Supprimer"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Footer avec jury et lien détails */}
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {soutenance.jury && (
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-1 text-gray-500" />
                <span>Jury: {soutenance.jury.nom || 'Non défini'}</span>
              </div>
            )}
          </div>
          
          <button
            onClick={() => onViewDetails(soutenance)}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center transition-colors"
          >
            Détails
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoutenanceCard; 